import { formatBrasilia } from '@/lib/brasiliaDate';
import type { TrialSummary, UserSubscription } from '@/services/account.service';

/**
 * Textos do período de teste (chamado 7176). A REGRA — se está em teste, até
 * quando, quantos dias faltam, o tom do aviso e a cotação — vem pronta do
 * backend no bloco `trial` de `GET /billing/my-subscription`; aqui só se
 * montam as frases exibidas.
 */

const diaEMes = (value: string): string => formatBrasilia(value, 'dd/MM');

const dias = (quantidade: number): string => (quantidade === 1 ? '1 dia' : `${quantidade} dias`);

export const trialRemainingText = (daysRemaining: number): string => {
  if (daysRemaining <= 0) return 'último dia';
  return daysRemaining === 1 ? 'falta 1 dia' : `faltam ${daysRemaining} dias`;
};

export const trialEndLabel = (trial: TrialSummary): string =>
  trial.has_ended
    ? `Período de teste encerrado em ${diaEMes(trial.trial_end)}`
    : `Teste grátis até ${diaEMes(trial.trial_end)} · ${trialRemainingText(trial.days_remaining)}`;

export const trialAfterEndText = (trial: TrialSummary): string => {
  const destino = trial.plan_after_trial_name ?? 'plano gratuito';
  return trial.has_ended
    ? `Sem o pagamento, você passa para o ${destino}.`
    : `Ao fim do teste, você passa para o ${destino}.`;
};

const CONVITE_PARA_ASSINAR = 'Assine para não perder seus recursos.';

/** Título do aviso do topo — mesmo formato da tarja de fatura vencida. */
export const trialNoticeTitle = (trial: TrialSummary): string => {
  if (trial.notice_level === 'last_day') return 'Último dia do seu período de teste!';
  if (trial.notice_level === 'ending') return 'Seu período de teste está acabando!';
  return 'Período de teste';
};

export const trialNoticeText = (trial: TrialSummary, planName: string): string => {
  if (trial.notice_level === 'last_day') return `Seu teste termina hoje. ${CONVITE_PARA_ASSINAR}`;
  if (trial.notice_level === 'ending') {
    return `Seu teste termina em ${dias(trial.days_remaining)}. ${CONVITE_PARA_ASSINAR}`;
  }
  return `Você está no período de teste do ${planName} até ${diaEMes(trial.trial_end)}.`;
};

/** Membro convidado vê a situação, mas só o titular assina. */
export const TRIAL_MEMBER_HINT = 'Peça ao titular da conta para assinar.';

/** Confirmação de assinar no teste — texto aprovado em 2026-10-15: destaca a
 * continuidade, sem falar em "perder" os dias restantes. */
export const TRIAL_SUBSCRIBE_COPY = {
  title: (planName: string): string => `Assinar o ${planName}`,
  description: (planName: string): string =>
    `Assine agora e continue usando todos os recursos do ${planName}, sem nenhuma interrupção.`,
  paymentToday: 'Pagamento hoje',
  nextCharge: 'Próxima cobrança',
  confirm: (amount: string): string => `Assinar e pagar ${amount}`,
  dismiss: 'Agora não',
  paymentNotice:
    'Sua assinatura é ativada assim que o pagamento for confirmado. Até lá, você continua com os recursos do período de teste.',
  annualNote:
    'No anual, a troca para o mensal ou para o plano gratuito fica disponível a partir da próxima cobrança.',
} as const;

/** Assinatura pedida no teste, ainda não paga — do próprio plano ou de outro.
 * Com um pedido aberto no teste só dá para pagar ou desistir (regra do backend). */
export const isTrialConversionPending = (subscription: UserSubscription | null | undefined): boolean =>
  !!subscription?.trial && !!subscription.pending_plan_id;

/** Desistir e "continuar no teste" só existe enquanto o teste vale: com ele já
 * vencido, desistir leva direto ao plano gratuito — e o texto diz isso. */
const desistirLevaAoGratuito = (subscription: UserSubscription): boolean => !!subscription.trial?.has_ended;

const planoDepoisDoTeste = (subscription: UserSubscription): string =>
  subscription.trial?.plan_after_trial_name ?? 'plano gratuito';

export const trialExitLabel = (subscription: UserSubscription): string =>
  desistirLevaAoGratuito(subscription)
    ? `Desistir e passar para o ${planoDepoisDoTeste(subscription)}`
    : TRIAL_PENDING_COPY.keepTrial;

export const trialExitToast = (subscription: UserSubscription): string =>
  desistirLevaAoGratuito(subscription)
    ? `Você passou para o ${planoDepoisDoTeste(subscription)}.`
    : 'Você continua no período de teste.';

/** Há valor para assinar agora (um dos ciclos com preço). Uma regra para os
 * dois "Assinar agora" da tela de planos. */
export const canSubscribeInTrial = (subscription: UserSubscription | null | undefined): boolean =>
  (subscription?.trial?.activation_quotes.length ?? 0) > 0;

export const TRIAL_PENDING_COPY = {
  title: 'Sua assinatura aguarda pagamento',
  description:
    'Sua assinatura é ativada assim que o pagamento for confirmado. Enquanto isso, você continua usando todos os recursos normalmente.',
  keepTrial: 'Continuar no período de teste',
} as const;
